import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tournamentService } from "../../services/api/tournamentService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import VenueSearch from "../ui/VenueSearch";
import { toDateOnlyString } from "../../utils/dateOnly";
import "../../styles/features/tournaments.css";
import { PriceHint } from "../payments/PriceField";
import { RefundPolicyField } from "../payments/RefundPolicy";

/**
 * The fields of an edit that differ from what was loaded. The form holds every
 * field, but sending unchanged structural fields after registration closed was
 * refused with 409 (TN-B08), and status is not an edit (TN-B06).
 */
export const changedFields = (data, initial) =>
  Object.fromEntries(
    Object.entries(data).filter(([key, value]) => {
      if (key === "bracket_settings") {
        return JSON.stringify(value || {}) !== JSON.stringify(initial?.[key] || {});
      }
      return String(value ?? "") !== String(initial?.[key] ?? "");
    })
  );

/** The update body the form produces (numbers parsed, auto_advance into bracket_settings). */
const toPayload = (data) => {
  const payload = {
    ...data,
    sport_id: parseInt(data.sport_id),
    max_participants: parseInt(data.max_participants),
    min_participants: parseInt(data.min_participants),
    entry_fee: parseFloat(data.entry_fee),
    prize_pool: parseFloat(data.prize_pool),
    num_courts: parseInt(data.num_courts) || 1,
    groups_count: parseInt(data.groups_count) || 2,
    teams_advancing_per_group: parseInt(data.teams_advancing_per_group) || 2,
    // "Any skill level" is no level (TN-B09)
    skill_level: data.skill_level || null,
    // Merged into the stored settings by the API (the draw and match format are kept)
    bracket_settings: { auto_advance: !!data.auto_advance },
  };
  delete payload.auto_advance;
  return payload;
};

const STATUS_LABELS = {
  draft: "Draft",
  registration_open: "Registration Open",
  registration_closed: "Registration Closed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const TournamentEdit = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sports, setSports] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFormatInfo, setShowFormatInfo] = useState(false);
  const [tournamentStatus, setTournamentStatus] = useState("draft");
  const [tournamentStartDate, setTournamentStartDate] = useState("");
  // The payload the loaded tournament would produce, to send only what changed
  const [initialPayload, setInitialPayload] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sport_id: "",
    tournament_type: "single_elimination",
    category: "competitive",
    skill_level: "",
    start_date: "",
    end_date: "",
    registration_start: "",
    registration_end: "",
    max_participants: 16,
    min_participants: 2,
    entry_fee: 0,
    prize_pool: 0,
    is_public: true,
    requires_approval: false,
    // Venue
    venue: "",
    venue_address: "",
    venue_lat: null,
    venue_lng: null,
    venue_place_id: "",
    // Courts
    num_courts: 1,
    // Groups + knockout layout
    groups_count: 2,
    teams_advancing_per_group: 2,
    auto_advance: true,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Load user's sports first, fallback to all sports
        const loadSports = async () => {
          try {
            const userRes = await getUserSports();
            const data = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
            if (Array.isArray(data) && data.length) return data;
          } catch { /* fall through */ }
          const allRes = await getAllSports();
          return Array.isArray(allRes.data) ? allRes.data : [];
        };
        const [sportsData, tournamentResponse] = await Promise.all([
          loadSports(),
          tournamentService.getTournamentById(id),
        ]);

        setSports(sportsData);

        // After backend fix: tournamentResponse.data = tournament object
        const tournament = tournamentResponse.data;

        const fmt = (d) => toDateOnlyString(d);

        const loaded = {
          name: tournament.name || "",
          description: tournament.description || "",
          sport_id: tournament.sport_id || "",
          tournament_type: tournament.tournament_type || "single_elimination",
          category: tournament.category || "competitive",
          skill_level: tournament.skill_level || "",
          start_date: fmt(tournament.start_date),
          end_date: fmt(tournament.end_date),
          registration_start: fmt(tournament.registration_start),
          registration_end: fmt(tournament.registration_end),
          max_participants: tournament.max_participants || 16,
          min_participants: tournament.min_participants || 2,
          entry_fee: tournament.entry_fee || 0,
          refund_policy: tournament.refund_policy || "",
          prize_pool: tournament.prize_pool || 0,
          is_public: tournament.is_public !== undefined ? tournament.is_public : true,
          requires_approval: tournament.requires_approval || false,
          // Venue
          venue: tournament.venue || "",
          venue_address: tournament.venue_address || "",
          venue_lat: tournament.venue_lat ?? null,
          venue_lng: tournament.venue_lng ?? null,
          venue_place_id: tournament.venue_place_id || "",
          // Courts
          num_courts: tournament.num_courts || 1,
          groups_count: tournament.groups_count || 2,
          teams_advancing_per_group: tournament.teams_advancing_per_group || 2,
          auto_advance: tournament.bracket_settings?.auto_advance !== false,
        };
        setFormData(loaded);
        setInitialPayload(toPayload(loaded));
        setTournamentStatus(tournament.status || "draft");
        setTournamentStartDate(fmt(tournament.start_date));
      } catch (err) {
        setError("Failed to load tournament data");
      } finally {
        setLoading(false);
      }
    };

    if (id) loadData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setError("");
    setSuccess("");
  };

  const handleVenueChange = (venueObj) => {
    setFormData((prev) => ({
      ...prev,
      venue: venueObj.venue || "",
      venue_address: venueObj.venue_address || "",
      venue_lat: venueObj.venue_lat ?? null,
      venue_lng: venueObj.venue_lng ?? null,
      venue_place_id: venueObj.venue_place_id || "",
    }));
  };

  // The draw is fixed once registration closes: sport, format, size, groups
  const structureLocked = !["draft", "registration_open"].includes(tournamentStatus);

  // Dates are locked once the tournament has started or is in a terminal state
  const dateLocked = ["in_progress", "completed", "cancelled"].includes(tournamentStatus) ||
    (tournamentStartDate && new Date() >= new Date(tournamentStartDate));

  const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push("Tournament name is required");
    if (!formData.sport_id) errors.push("Sport selection is required");
    if (!formData.start_date) errors.push("Start date is required");
    if (!formData.end_date) errors.push("End date is required");
    // A one-day tournament starts and ends on the same date (TN-B14)
    if (formData.start_date && formData.end_date &&
      new Date(formData.start_date) > new Date(formData.end_date))
      errors.push("End date cannot be before the start date");
    if (parseInt(formData.max_participants) < parseInt(formData.min_participants))
      errors.push("Maximum participants cannot be less than minimum");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) { setError(validationErrors.join(". ")); return; }

    try {
      setSaving(true);
      setError("");
      const tournamentData = changedFields(toPayload(formData), initialPayload);
      if (Object.keys(tournamentData).length === 0) {
        setSuccess("Nothing to save.");
        return;
      }
      await tournamentService.updateTournament(id, tournamentData);
      setSuccess("Tournament updated successfully!");
      setTimeout(() => navigate(`/tournaments/${id}`), 2000);
    } catch (err) {
      setError(err.message || "Failed to update tournament");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "0.65rem 1rem",
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(99,102,241,0.3)",
    borderRadius: "0.5rem",
    color: "#e2e8f0",
    fontSize: "0.95rem",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "0.4rem",
    color: "#a78bfa",
    fontWeight: 600,
    fontSize: "0.9rem",
  };

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem" };

  const checkboxRowStyle = { display: "flex", alignItems: "center", gap: "0.6rem", color: "#e2e8f0" };

  if (loading) {
    return (
      <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
        <div className="tournaments-gaming-layout">
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-1">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>Loading Tournament...</h3>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
      <div className="tournaments-gaming-layout">

        {/* Header */}
        <div className="tournaments-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="tournaments-title-gaming">✏️ Edit Tournament</h1>
                <p className="tournaments-subtitle-gaming">Update tournament details and settings</p>
              </div>
              <div className="header-actions-gaming">
                <FloatingButton variant="outline" size="medium" onClick={() => navigate(`/tournaments/${id}`)} icon="←">
                  <span className="btn-text">Back to Details</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Scrollable body */}
        <div className="tournament-edit-scroll-body" style={{ flex: 1, overflowY: "auto", paddingRight: "0.25rem" }}>

        {/* Alerts */}
        {error && (
          <div className="gaming-message-floating error">
            <span className="message-icon">❌</span>
            <span className="message-text">{error}</span>
          </div>
        )}
        {success && (
          <div className="gaming-message-floating success">
            <span className="message-icon">✅</span>
            <span className="message-text">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Basic Info */}
          <FloatingCard variant="secondary" size="large" className="delay-2" style={{ marginBottom: "1.5rem" }}>
            <h3 className="form-section-title">🏆 Basic Information</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", marginTop: "1rem" }}>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Tournament Name *</label>
                  <input style={inputStyle} type="text" name="name" value={formData.name} onChange={handleInputChange} required maxLength="255" />
                </div>
                <div>
                  <label style={labelStyle}>Sport *</label>
                  <select style={inputStyle} name="sport_id" value={formData.sport_id} onChange={handleInputChange} required disabled={structureLocked}>
                    <option value="">Select a sport</option>
                    {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }} name="description" value={formData.description} onChange={handleInputChange} rows="3" maxLength="1000" />
              </div>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>
                    Tournament Type{" "}
                    <button
                      type="button"
                      onClick={() => setShowFormatInfo((v) => !v)}
                      style={{ background: "none", border: "none", color: "#a78bfa", cursor: "pointer", fontSize: "0.85rem", padding: "0 0.2rem" }}
                      title="Learn about tournament formats"
                    >
                      ℹ️
                    </button>
                  </label>
                  <select style={inputStyle} name="tournament_type" value={formData.tournament_type} onChange={handleInputChange} disabled={structureLocked}>
                    <option value="single_elimination">🏆 Single Elimination</option>
                    <option value="double_elimination">🔄 Double Elimination</option>
                    <option value="round_robin">🔁 Round Robin</option>
                    <option value="swiss_system">♟️ Swiss System</option>
                    <option value="group_knockout">🧩 Groups + Knockout</option>
                  </select>
                  {showFormatInfo && (
                    <div style={{ marginTop: "0.5rem", padding: "0.7rem", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "0.4rem", fontSize: "0.8rem", color: "#94a3b8", lineHeight: 1.6 }}>
                      {{
                        single_elimination: "🏆 One loss and you're out. Fast bracket. Top seeds meet only in the final. Best for 8–64 teams.",
                        double_elimination: "🔄 Teams get a second chance via the Losers Bracket. Best for competitive leagues.",
                        round_robin: "🔁 Everyone plays everyone. Most consistent team wins. Best for 4–12 teams.",
                        swiss_system: "♟️ Fixed rounds, no elimination. Participants on the same score meet, never the same opponent twice. Best for large tournaments.",
                        group_knockout: "🧩 Round-robin groups, then the top finishers of each group play a seeded knockout.",
                      }[formData.tournament_type]}
                    </div>
                  )}
                  {formData.tournament_type === "group_knockout" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginTop: "0.75rem" }}>
                      <div>
                        <label style={labelStyle} htmlFor="groups_count">Groups</label>
                        <input style={inputStyle} id="groups_count" type="number" min="1" max="32" name="groups_count" value={formData.groups_count} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label style={labelStyle} htmlFor="teams_advancing_per_group">Advancing per group</label>
                        <input style={inputStyle} id="teams_advancing_per_group" type="number" min="1" max="32" name="teams_advancing_per_group" value={formData.teams_advancing_per_group} onChange={handleInputChange} />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {/* Status changes through the tournament's actions (open/close registration, start, complete, cancel) — TN-B06 */}
                  <label style={labelStyle}>Status</label>
                  <div style={{ ...inputStyle, opacity: 0.75 }} data-testid="tournament-status">
                    {STATUS_LABELS[tournamentStatus] || tournamentStatus}
                  </div>
                </div>
              </div>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select style={inputStyle} name="category" value={formData.category} onChange={handleInputChange}>
                    <option value="friendly">🤝 Friendly</option>
                    <option value="competitive">⚔️ Competitive</option>
                    <option value="ranked">🏅 Ranked</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Skill Level</label>
                  <select style={inputStyle} name="skill_level" value={formData.skill_level || ""} onChange={handleInputChange}>
                    <option value="">Any skill level</option>
                    <option value="beginner">🌿 Beginner</option>
                    <option value="recreational">🌱 Recreational</option>
                    <option value="intermediate">⚡ Intermediate</option>
                    <option value="advanced">🔥 Advanced</option>
                    <option value="professional">👑 Professional</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Number of Courts / Fields</label>
                  <input style={inputStyle} type="number" name="num_courts" value={formData.num_courts} onChange={handleInputChange} min="1" />
                </div>
              </div>
            </div>
          </FloatingCard>

          <FloatingCard variant="secondary" size="large" className="delay-3" style={{ marginBottom: "1.5rem" }}>
            <h3 className="form-section-title">📅 Dates &amp; Schedule</h3>
            {dateLocked && (
              <div style={{ marginTop: "0.75rem", padding: "0.65rem 1rem", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: "0.4rem", color: "#f59e0b", fontSize: "0.85rem" }}>
                🔒 Start and end dates cannot be changed once the tournament has started.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", marginTop: "1rem" }}>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Start Date *</label>
                  <input style={{ ...inputStyle, ...(dateLocked ? { opacity: 0.55, cursor: "not-allowed" } : {}) }} type="date" name="start_date" value={formData.start_date} onChange={handleInputChange} required disabled={dateLocked} />
                </div>
                <div>
                  <label style={labelStyle}>End Date *</label>
                  <input style={{ ...inputStyle, ...(dateLocked ? { opacity: 0.55, cursor: "not-allowed" } : {}) }} type="date" name="end_date" value={formData.end_date} onChange={handleInputChange} required disabled={dateLocked} />
                </div>
              </div>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Registration Opens</label>
                  <input style={inputStyle} type="date" name="registration_start" value={formData.registration_start} onChange={handleInputChange} />
                </div>
                <div>
                  <label style={labelStyle}>Registration Closes</label>
                  <input style={inputStyle} type="date" name="registration_end" value={formData.registration_end} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </FloatingCard>

          {/* Participants */}
          <FloatingCard variant="secondary" size="large" className="delay-4" style={{ marginBottom: "1.5rem" }}>
            <h3 className="form-section-title">👥 Participants</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", marginTop: "1rem" }}>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Min Participants</label>
                  <input style={inputStyle} type="number" name="min_participants" value={formData.min_participants} onChange={handleInputChange} min="2" max="256" />
                </div>
                <div>
                  <label style={labelStyle}>Max Participants</label>
                  <input style={inputStyle} type="number" name="max_participants" value={formData.max_participants} onChange={handleInputChange} min="2" max="256" disabled={structureLocked} />
                </div>
              </div>
            </div>
          </FloatingCard>

          {/* Venue */}
          <FloatingCard variant="secondary" size="large" className="delay-4" style={{ marginBottom: "1.5rem" }}>
            <h3 className="form-section-title">📍 Venue</h3>
            <div style={{ marginTop: "1rem" }}>
              <VenueSearch
                label="Tournament Venue"
                hideCourt={true}
                value={{
                  venue: formData.venue,
                  venue_address: formData.venue_address,
                  venue_lat: formData.venue_lat,
                  venue_lng: formData.venue_lng,
                  venue_place_id: formData.venue_place_id,
                }}
                onChange={handleVenueChange}
              />
            </div>
          </FloatingCard>

          {/* Financial & Settings */}
          <FloatingCard variant="secondary" size="large" className="delay-5" style={{ marginBottom: "1.5rem" }}>
            <h3 className="form-section-title">💰 Financial &amp; Settings</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", marginTop: "1rem" }}>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Entry Fee ($)</label>
                  <input style={inputStyle} type="number" name="entry_fee" value={formData.entry_fee} onChange={handleInputChange} min="0" step="0.01" />
                  <PriceHint value={formData.entry_fee} payer="Captains" hint="0 = free entry." />
                </div>
                <div>
                  <label style={labelStyle}>Prize Pool ($)</label>
                  <input style={inputStyle} type="number" name="prize_pool" value={formData.prize_pool} onChange={handleInputChange} min="0" step="0.01" />
                </div>
              </div>
              <RefundPolicyField
                value={formData.refund_policy}
                price={formData.entry_fee}
                onChange={(refund_policy) => setFormData((prev) => ({ ...prev, refund_policy }))}
              />
              <div style={rowStyle}>
                <label style={checkboxRowStyle}>
                  <input type="checkbox" name="is_public" checked={formData.is_public} onChange={handleInputChange} />
                  <span>Public Tournament</span>
                </label>
                <label style={checkboxRowStyle}>
                  <input type="checkbox" name="requires_approval" checked={formData.requires_approval} onChange={handleInputChange} />
                  <span>Requires Approval</span>
                </label>
                {formData.tournament_type !== "round_robin" && (
                  <label style={checkboxRowStyle}>
                    <input type="checkbox" name="auto_advance" checked={formData.auto_advance} onChange={handleInputChange} />
                    <span>Create next matches automatically</span>
                  </label>
                )}
              </div>
            </div>
          </FloatingCard>

          {/* Actions */}
          <FloatingCard variant="outline" size="large" className="delay-5">
            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <FloatingButton variant="secondary" size="medium" onClick={() => navigate(`/tournaments/${id}`)} disabled={saving} type="button" icon="✕">
                <span className="btn-text">Cancel</span>
              </FloatingButton>
              <FloatingButton variant="success" size="medium" type="submit" disabled={saving} icon="💾">
                <span className="btn-text">{saving ? "Saving…" : "Save Changes"}</span>
              </FloatingButton>
            </div>
          </FloatingCard>

        </form>
        </div>{/* end scrollable body */}
      </div>
    </GameLayout>
  );
};

export default TournamentEdit;
